import React from 'react';
import { TFunction } from 'i18next';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import styled from 'styled-components/macro';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { Link } from 'react-router-dom';
import Hr from '../../../components/Hr';
import { distances, border, colors } from '../../../styles/constants';
import { DateTime, Money } from '../../../components/Formatters';
import Card from '../../../components/Card';
import Pan from '../../../components/Formatters/Pan';
import { AccountAdminUser } from '../../../types/management-auth';

import { H3, P } from '../../../components/Typography';
import {
    OrderWithEvents,
    Order,
    OrderAuthorization,
    OrderCapture,
    OrderRefund,
    OrderCancellation,
    ApiEvent,
    OrderEvent
} from '../../../types/orders';
import TransactionId from '../../common/TransactionId';
import ExpandCollapseJson from '../../common/ExpandCollapseJson';
import CreatedBy from '../../common/CreatedBy';
import { parseISOStrictOr } from '../../../helpers/date';
import { StyledRouteLink } from '../../../components/StyledLink';

interface OrderEventFunctionParams {
    t: TFunction;
    event: OrderEvent;
    order: OrderWithEvents;
    i: number;
}

interface ApiEventFunctionParams {
    t: TFunction;
    event: ApiEvent;
    order: OrderWithEvents;
    i: number;
}

const Item = (
    {
        t,
        item,
        order,
    }: {
        t: TFunction;
        item: { line_id: number; amount: number };
        order: OrderWithEvents;
    }
) => {
    return (
        <ItemField>
            <span>
                {t('orders.fields.line_item_id')} {item.line_id}
            </span>
            {item.amount !== undefined && <Money amount={item.amount} currency={order.currency} />}
        </ItemField>
    );
};

const getProcessedDelayMinutes = (processedAt: string | undefined, createdAt: string | undefined) => {
    return differenceInMinutes(
        parseISOStrictOr(processedAt, new Date(0)),
        parseISOStrictOr(createdAt, new Date(0))
    );
};

const PaymentOperationSummary = (
    {
        t,
        order,
        event,
        body,
    }: {
        t: TFunction;
        order: OrderWithEvents;
        event: ApiEvent;
        body: OrderAuthorization | OrderCapture | OrderRefund | OrderCancellation;
    }
) => (<>
    {body.payment_details?.payment_id && (
        <>
            <Field>
                {t('orders.order.history.events.payment_id')}{' '}
                <TransactionId transactionId={body.payment_details?.payment_id} />
            </Field>
            <br />
        </>
    )}
    {body.processed_by && (
        <Field>
            {t('orders.order.history.events.operation_processed', {
                processed_by: body.processed_by,
            })}{' '}
            {getProcessedDelayMinutes(body.processed_at, event.created_at) > 1 && (
                <DateTime isoString={body.processed_at || ''} boldTime={true} />
            )}
        </Field>
    )}
    {body.payment_details && (
        <>
            <Field>
                {[
                    body.payment_details.psp,
                    body.payment_details.payment_product &&
                        t([
                            `payments.payment_product.${body.payment_details.payment_product}`,
                            body.payment_details.payment_product,
                        ]),
                    body.payment_details.payment_product_type &&
                        t([
                            `payments.product_types.${body.payment_details.payment_product_type}`,
                            body.payment_details.payment_product_type,
                        ]),
                ]
                    .reduce<string[]>(
                        (acc, elem) =>
                            // remove empty and duplicate entries
                            !elem || acc.includes(elem) ? acc : [...acc, elem],
                        []
                    )
                    .join(', ')}
            </Field>

            {body.payment_details.card && (
                <Field>
                    {[body.payment_details.card.brand, body.payment_details.card.type].filter((x) => x).join(', ')}

                    {body.payment_details.card.masked_pan && (
                        <>
                            {body.payment_details.card.brand || body.payment_details.card.type ? ', ' : ''}
                            <Pan value={body.payment_details.card.masked_pan} />
                        </>
                    )}
                </Field>
            )}
        </>
    )}
    <br />
    {body.items.map((item, j) => (
        <Item key={j} t={t} item={item} order={order} />
    ))}
    <ItemField>
        <strong>{t('orders.fields.total')}</strong>
        <strong>
            <Money
                amount={body.items.reduce((sum, item) => sum + (item.amount || 0), 0)}
                currency={order.currency}
            />
        </strong>
    </ItemField>
</>);

const REQUESTS = {
    COMPLETE_DRAFT: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/draft_orders\/[a-zA-Z0-9_\-.]+\/complete\/?$/,
        method: 'PUT',
        header: ({ t, event, order, i }: ApiEventFunctionParams) =>
            i === 0 ? (
                <>{t('orders.order.history.events.created')}</>
            ) : (
                <>{t('orders.order.history.events.updated_by_draft')}</>
            ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: any) => (
            <Trans t={t} i18nKey="orders.order.history.events.draft_id_link" values={{ draft_id: event.draft_id }}>
                From draft <StyledRouteLink to={`../drafts/${event.draft_id}`}>{event.draft_id}</StyledRouteLink>
            </Trans>
        ),
    },
    UPDATE_ORDER: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/?$/,
        method: 'PUT',
        header: ({ t, event, order, i }: ApiEventFunctionParams, body: Order) => (
            <>{t('orders.order.history.events.updated')}</>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: Order) => (
            <ExpandCollapseJson label={t('orders.order.history.events.updated_fields')} json={body} />
        ),
    },
    OPEN_ORDER: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/open\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => <>{t('orders.order.history.events.open')}</>,
        summary: undefined,
    },
    CANCEL_ORDER: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/cancel\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => <>{t('orders.order.history.events.cancel')}</>,
        summary: undefined,
    },
    CLOSE_ORDER: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/close\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => <>{t('orders.order.history.events.close')}</>,
        summary: undefined,
    },
    POST_EVENT: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/events\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => <>{t('orders.order.history.events.post_event')}</>,
        summary: undefined,
    },
    POST_COMMENT: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/comments\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => (
            <>{t('orders.order.history.events.post_comment')}</>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: any) => <></>,
    },
    DELETE_COMMENT: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/comments\/[a-zA-Z0-9_\-.]+\/?$/,
        method: 'DELETE',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => (
            <>{t('orders.order.history.events.delete_comment')}</>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: any) => <></>,
    },
    POST_SESSION: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/sessions\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams) => (
            <>{t('orders.order.history.events.post_session')}</>
        ),
        summary: undefined,
    },
    POST_AUTHORIZATION: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/authorizations\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderAuthorization) => (
            <>
                {body.success === false && <>{t('orders.order.history.events.failed')}: </>}
                {t('orders.order.history.events.post_authorization')}
            </>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderAuthorization) => (
            <PaymentOperationSummary t={t} order={order} event={event} body={body} />
        ),
    },
    POST_CAPTURE: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/captures\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderCapture) => (
            <>
                {body.success === false && <>{t('orders.order.history.events.failed')}: </>}
                {t('orders.order.history.events.post_capture')}
            </>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderCapture) => (
            <PaymentOperationSummary t={t} order={order} event={event} body={body} />
        ),
    },
    POST_REFUND: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/refunds\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderRefund) => (
            <>
                {body.success === false && <>{t('orders.order.history.events.failed')}: </>}
                {t('orders.order.history.events.post_refund')}
            </>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderRefund) => (
            <PaymentOperationSummary t={t} order={order} event={event} body={body} />
        ),
    },
    POST_CANCELLATION: {
        path: /^\/v1\/accounts\/[PT]\d{8}\/shopping\/orders\/[a-zA-Z0-9_\-.]+\/cancellations\/?$/,
        method: 'POST',
        header: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderCancellation) => (
            <>
                {body.success === false && <>{t('orders.order.history.events.failed')}: </>}
                {t('orders.order.history.events.post_cancellation')}
            </>
        ),
        summary: ({ t, event, order, i }: ApiEventFunctionParams, body: OrderCancellation) => (
            <PaymentOperationSummary t={t} order={order} event={event} body={body} />
        ),
    },
};

interface Request {
    path?: string;
    method?: string;
    body?: any;
}

const safeJsonParse = (body: string | undefined) => {
    try {
        return body ? JSON.parse(body) : undefined;
    } catch {
        console.log('Failed JSON.parse of', body);
        return null;
    }
};

const getRequest = (event: ApiEvent): Request => {
    const { path, method, body } = event?.request || {};
    return {
        path,
        method,
        body: safeJsonParse(body),
    };
};

const matches = (method: string, path: RegExp, request: Request) => {
    const chopped = (request.path || '').split('?')[0];
    return method === request.method && path.test(chopped);
};

const getStatusTitle = (params: OrderEventFunctionParams) => {
    const { t, event } = params;
    if (event.event_type === 'api-event') {
        const request = getRequest(event);
        const handler = Object.values(REQUESTS).find((handler) => matches(handler.method, handler.path, request));
        if (handler) {
            return handler.header(params as ApiEventFunctionParams, request.body);
        }
    }
    return <>{t('orders.order.history.generic_event', { name: event.id })}</>;
};

const ApiEventSummary = (params: ApiEventFunctionParams) => {
    const { event, t } = params;
    const request = getRequest(event);
    const handler = Object.values(REQUESTS).find((handler) => matches(handler.method, handler.path, request));
    if (handler && handler.summary) {
        return handler.summary(params as ApiEventFunctionParams, request.body);
    } else if (request && request.body) {
        return (
            <>
                <em>
                    {request.method} {request.path}
                </em>
                <br />
                <ExpandCollapseJson
                    label={t('orders.order.history.events.event_request_payload')}
                    json={request.body}
                />
            </>
        );
    }
    return null;
};

export interface OrderHistoryProps extends WithTranslation {
    order: OrderWithEvents;
    users: AccountAdminUser[];
}

const OrderHistory = (
    {
        t,
        order,
        users,
    }: OrderHistoryProps
) => {
    const events: OrderEvent[] = order.events || [];
    const closed = order.closed_at;
    const deleted = order.deleted_at;
    const expires = order.time_to_live_expires_at;
    const showEmpty = events.length === 0 && !deleted;
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.order.history.title')}</H3>
            <Hr />
            <div>
                {events.map((event: OrderEvent, i: number) => {
                    return (
                        <EventWrapper key={i}>
                            <Dot className={i === events.length - 1 && !closed ? 'active dot' : 'dot'} />
                            <H3>{getStatusTitle({ t, event, order, i })}</H3>
                            <DateTimeWrapper>
                                <DateTime isoString={event.created_at || ''} boldTime />{' '}
                                <By>
                                    {t('orders.order.history.created_by')} <CreatedBy createdBy={event.created_by} users={users} />
                                </By>
                            </DateTimeWrapper>
                            {event.event_type === 'service-event' && (
                                <>
                                    <Field>
                                        {t('orders.order.history.events.state')}: <code>{event.state}</code>
                                    </Field>
                                    <Field>
                                        {t('orders.order.history.events.service')}: <code>{event.service}</code>
                                    </Field>
                                    <Field>
                                        {t('orders.order.history.events.service_status')}:{' '}
                                        <code>{event.service_status}</code>
                                    </Field>
                                </>
                            )}
                            {event.description && (
                                <>
                                    <br />
                                    <Field>{event.description}</Field>
                                </>
                            )}

                            {event.event_type === 'api-event' && (
                                <>
                                    <br />
                                    <Field>
                                        <ApiEventSummary i={i} t={t} event={event} order={order} />
                                    </Field>
                                </>
                            )}

                            {event.event_type === 'service-event' && event.metadata && (
                                <>
                                    <br />
                                    <Field>
                                        <ExpandCollapseJson
                                            label={t('orders.order.history.events.metadata')}
                                            json={event.metadata}
                                        />
                                    </Field>
                                </>
                            )}
                        </EventWrapper>
                    );
                })}
                {expires && (
                    <EventWrapper>
                        <Dot className={'dot'} />
                        <H3>{order.closed_at ? t('orders.order.expired') : t('orders.order.expires')}</H3>
                        <DateTime isoString={order.time_to_live_expires_at || ''} boldTime />{' '}
                    </EventWrapper>
                )}
                {closed && (
                    <>
                        <EventWrapper>
                            <Dot className={'active dot'} />
                            <H3>{t('orders.order.closed')}</H3>
                            <DateTime isoString={order.closed_at || ''} boldTime />{' '}
                            <DateTimeWrapper>
                                <By>
                                    {t('orders.draft_order.history.created_by')} <CreatedBy createdBy={order.closed_by} />
                                </By>
                            </DateTimeWrapper>
                        </EventWrapper>
                    </>
                )}
                {deleted && (
                    <>
                        <EventWrapper>
                            <Dot className={'active dot'} />
                            <H3>{t('orders.order.deleted')}</H3>
                            <DateTime isoString={order.deleted_at || ''} boldTime />{' '}
                            <DateTimeWrapper>
                                <By>
                                    {t('orders.draft_order.history.created_by')} <CreatedBy createdBy={order.deleted_by} />
                                </By>
                            </DateTimeWrapper>
                        </EventWrapper>
                    </>
                )}
                {showEmpty && <P>{t('orders.order.history.empty')}</P>}
            </div>
        </Card>
    );
};

export default withTranslation()(OrderHistory);



const By = styled.span`
    display: inline-block;
    margin-left: ${distances.small};
`;

const Dot = styled.div`
    position: absolute;
    top: ${distances.tiny};
    left: -6px;
    height: ${distances.small};
    width: ${distances.small};
    border-radius: ${distances.small};
    background: rgba(242, 242, 242, 1);

    &:after {
        content: ' ';
        position: absolute;
        top: 4px;
        left: 4px;
        height: ${distances.tiny};
        width: ${distances.tiny};
        border-radius: ${distances.tiny};
        background: #46535e;
        opacity: 0;
    }
    &.active:after {
        opacity: 1;
    }
`;

const EventWrapper = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 22px;
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #46535e;

    &:before {
        content: ' ';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:first-child:before {
        content: ' ';
        position: absolute;
        top: ${distances.small};
        left: 0;
        bottom: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:first-child:last-child:before {
        display: none;
    }

    &:last-child:before {
        content: ' ';
        position: absolute;
        top: 0;
        height: 10px;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
`;

const Field = styled.div`
    display: block;
    word-break: normal;
    white-space: nowrap;
`;

const ItemField = styled.div`
    display: flex;
    justify-content: space-between;
    word-break: normal;
    white-space: nowrap;
    border-bottom: 1px dashed transparent;

    &:hover {
        border-bottom: 1px dashed #cecece;
    }
`;

const DateTimeWrapper = styled.div`
    font-weight: 400;
    line-height: ${distances.small};
    font-feature-settings: 'tnum';
`;
